import React, { useEffect, useState } from 'react';
import { getTransactions, getTransaction } from '../api/transactions';
import { Transaction } from '../types/models';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Typography, IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerTx, setDrawerTx] = useState<Transaction | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  useEffect(() => {
    getTransactions()
      .then(setTransactions)
      .catch((err) => setError(err.message || 'Failed to load transactions'))
      .finally(() => setLoading(false));
  }, []);

  const handleDetailClick = (id: number) => {
    setDrawerOpen(true);
    setDrawerId(id);
    setDrawerTx(null);
    setDrawerLoading(true);
    setDrawerError(null);
    getTransaction(id)
      .then(setDrawerTx)
      .catch((err) => setDrawerError(err.message || 'Failed to load transaction'))
      .finally(() => setDrawerLoading(false));
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerId(null);
    setDrawerTx(null);
    setDrawerLoading(false);
    setDrawerError(null);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <TableContainer component={Paper}>
      <Typography variant="h5" sx={{ m: 2 }}>Transactions</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Source Address</TableCell>
            <TableCell>Target Address</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Occurred At</TableCell>
            <TableCell>Hash</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx) => (
            <TableRow key={tx.id} hover>
              <TableCell>{tx.id}</TableCell>
              <TableCell>{tx.source_address}</TableCell>
              <TableCell>{tx.target_address}</TableCell>
              <TableCell>{tx.amount}</TableCell>
              <TableCell>{tx.occurred_at ? new Date(tx.occurred_at).toLocaleString() : ''}</TableCell>
              <TableCell>{tx.transaction_hash}</TableCell>
              <TableCell>{tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}</TableCell>
              <TableCell>
                <IconButton onClick={() => handleDetailClick(tx.id)} size="small">
                  <InfoIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { width: 340, p: 2 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Transaction Details</Typography>
          {drawerLoading ? (
            <CircularProgress />
          ) : drawerError ? (
            <Alert severity="error">{drawerError}</Alert>
          ) : drawerTx && (
            <>
              <Typography variant="body2"><b>ID:</b> {drawerTx.id}</Typography>
              <Typography variant="body2"><b>Source Address:</b> {drawerTx.source_address}</Typography>
              <Typography variant="body2"><b>Target Address:</b> {drawerTx.target_address}</Typography>
              <Typography variant="body2"><b>Amount:</b> {drawerTx.amount}</Typography>
              <Typography variant="body2"><b>Occurred At:</b> {drawerTx.occurred_at ? new Date(drawerTx.occurred_at).toLocaleString() : ''}</Typography>
              <Typography variant="body2"><b>Hash:</b> {drawerTx.transaction_hash}</Typography>
              <Typography variant="body2"><b>Created At:</b> {drawerTx.created_at ? new Date(drawerTx.created_at).toLocaleString() : ''}</Typography>
              <Typography variant="body2"><b>Updated At:</b> {drawerTx.updated_at ? new Date(drawerTx.updated_at).toLocaleString() : ''}</Typography>
            </>
          )}
        </Box>
      </Drawer>
    </TableContainer>
  );
};

export default TransactionsPage; 